import * as Sentry from '@sentry/react';
import {
  getViewportApiKey,
  localStorageGetWithExpiry,
  localStorageSetWithExpiry,
  parseJwt,
} from '@vindr/utils/helpers/common.helper';
import {
  FEATURES_KEY,
  PERMISSION_TOKEN,
  REFRESH_TOKEN,
  TOKEN,
} from './constants/auth.constant';
import { CONFIG_SERVER, PERMISSION_LOADING } from './constants/common.constant';
import get from 'lodash/get';
import { Dispatch, SetStateAction } from 'react';
import {
  fetchPermissionToken,
  fetchTokenFromCode,
  refreshToken,
} from './apis/auth.api';
import { ResponseUserInfo, getUserInfo } from './apis/user.api';

class Keycloak {
  public permissions: {
    rsid?: string;
    rsname?: string;
    scopes?: string[];
  };
  public features: any[];
  public userInfo: ResponseUserInfo['data'] | null;
  public initiated: boolean;
  public initiating: boolean;
  public dismissUpdateInfo: boolean;
  public clientId: string;
  public authUrl: string;
  public permissionCache: Record<string, any>;
  setState: Dispatch<SetStateAction<Keycloak>>;
  private listener: (() => void) | null = null;

  constructor() {
    this.permissions = { scopes: [PERMISSION_LOADING] };
    this.features = [];
    this.userInfo = null;
    this.initiated = false;
    this.dismissUpdateInfo = false;
    this.authUrl = CONFIG_SERVER.AUTH_URL + '/auth';
    this.clientId = CONFIG_SERVER.CLIENT_ID;
  }

  // Getter

  getRealmId = () => CONFIG_SERVER.REALM_ID;
  getAccessToken = () => localStorageGetWithExpiry(TOKEN);
  getRefreshToken = () => localStorageGetWithExpiry(REFRESH_TOKEN);
  getPermissionToken = () => localStorageGetWithExpiry(PERMISSION_TOKEN);
  getPermissions = () => {
    const permissionToken = this.getPermissionToken();
    if (permissionToken && this.permissionCache?.[permissionToken]) {
      return this.permissionCache[permissionToken]?.scopes;
    } else if (permissionToken) {
      this.setPermissionCache(permissionToken);
      return this.permissionCache[permissionToken]?.scopes;
    }
    return [];
  };
  getFeatures = () => JSON.parse(localStorage.getItem(FEATURES_KEY));
  getUserInfo = () => this.userInfo;
  getDismissUpdateInfo = () => this.dismissUpdateInfo;
  isSuperAdmin = () => Boolean(this.getUserInfo()?.is_super);

  // Setter
  setAccessToken = (token: string, expiresIn: number) => {
    localStorageSetWithExpiry(
      TOKEN,
      token,
      new Date((expiresIn || 1800) * 1000 + Date.now()).getTime()
    );
  };

  setRefreshToken = (token: string, expiresIn: number) => {
    localStorageSetWithExpiry(
      REFRESH_TOKEN,
      token,
      new Date((expiresIn || 1800) * 1000 + Date.now()).getTime()
    );
  };

  setPermissionToken = (token: string, expiresIn: number) => {
    localStorageSetWithExpiry(
      PERMISSION_TOKEN,
      token,
      new Date((expiresIn || 1800) * 1000 + Date.now()).getTime()
    );
  };

  setFeatures = (features: any) => {
    this.features = features;
    this.notify();
    return localStorage.setItem(FEATURES_KEY, JSON.stringify(features));
  };

  setUserInfo = (userInfo: ResponseUserInfo['data']) => {
    this.userInfo = userInfo;
    this.notify();
  };

  setDismissUpdateInfo = (dismissUpdateInfo: boolean) => {
    this.dismissUpdateInfo = dismissUpdateInfo;
    this.notify();
  };

  setPermissionCache = (permissionToken) => {
    const parseInfo = parseJwt(permissionToken);
    const permissionsList = get(parseInfo, 'authorization.permissions') || [];
    const permissions =
      permissionsList.find((it) => it?.rsname === 'api') || permissionsList[0];

    console.log('PERMISSONS', {
      ...permissions,
      scopes: permissions?.scopes?.sort((a, b) => a.localeCompare(b)),
    });
    this.permissionCache = {
      [permissionToken]: permissions,
    };
    this.notify();
  };

  // Helper
  getCodeAndState() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const sessionState = urlParams.get('session_state');

    return { code, sessionState };
  }

  hasAllOfPerm = (perm_list: any[]) => {
    if (CONFIG_SERVER.DISABLE_AUTH) return true;

    const permissions = this.getPermissions();

    if (!permissions?.length) {
      // console.log('NO_PERM: User has no permissions');
      return false;
    }

    if (!perm_list?.length) {
      console.log('NO_PERM: Empty permission list');
      return false;
    }

    for (const perm of perm_list) {
      if (typeof perm === 'object' && perm !== null) {
        const list = Object.values(perm);

        if (list.some((p) => typeof p !== 'string')) {
          console.log('NO_PERM: Invalid nested permission format');
          return false;
        }

        const check = this.hasAllOfPerm(list);

        if (!check) {
          console.log('NO_PERM: Failed nested permission check', list);
          return false;
        }
      } else if (typeof perm === 'string') {
        if (!permissions.includes(perm)) {
          console.log('NO_PERM: Missing permission', perm);
          return false;
        }
      } else {
        console.log('NO_PERM: Invalid permission format', perm);
        return false;
      }
    }
    return true;
  };

  updateUserInfo = () => {
    getUserInfo().then((data) => (this.setUserInfo(data), this.notify()));
  };

  hasOneOfPerm = (perm_list: string[]) => {
    if (CONFIG_SERVER.DISABLE_AUTH) return true;

    const permissions = this.getPermissions();

    if (!perm_list?.length) {
      console.log('NO_PERM: Empty permission list');
      return false;
    }

    if (!permissions?.length) {
      // console.log('NO_PERM: User has no permissions');
      return false;
    }

    for (const perm of perm_list) {
      if (typeof perm === 'object' && perm !== null) {
        // if perm is object, check all perm in object
        const list = Object.values(perm);
        const check = this.hasAllOfPerm(list);

        if (check) return true;
      } else if (typeof perm === 'string') {
        if (permissions.includes(perm)) {
          return true;
        }
      } else continue;
    }

    console.log('NO_PERM: None of the required permissions found', perm_list);
    return false;
  };

  hasFeature = (feature) => {
    if (!feature) return true;
    const features = this.getFeatures();

    if (!features?.length) return false;

    return features.includes(feature);
  };

  init = async (callback = null) => {
    if (this.initiated || this.initiating) return;
    const apiKey = getViewportApiKey();

    if (apiKey) return;

    this.initiating = true;

    this.updateToken()
      .then(() => {
        console.log('AUTH INITIATED');
        this.initiated = true;
        this.initiating = false;
        if (callback) callback();
        this.notify();
      })
      .catch((err) => {
        console.log('AUTH INIT FAILED: ', err);
      });
  };

  requestLogin = (isRedirect: boolean) => {
    console.log('REQUEST LOGIN');
    if (!this.getRealmId()) {
      console.warn('REALM NOT FOUND');

      return;
    }

    if (!CONFIG_SERVER.AUTH_URL) {
      console.warn('AUTH URL NOT FOUND');

      return;
    }

    const pathAuth =
      CONFIG_SERVER.AUTH_URL +
      `/auth/realms/${this.getRealmId()}/protocol/openid-connect/auth`;

    let loginUrl =
      pathAuth +
      '?client_id=' +
      this.clientId +
      '&response_type=' +
      CONFIG_SERVER.RESPONSE_TYPE +
      '&state=' +
      CONFIG_SERVER.STATE;

    if (isRedirect) {
      loginUrl += '&redirect_uri=' + window.location.origin;
      sessionStorage.setItem('redirect_uri', window.location.href);
    }

    window.location.href = encodeURI(loginUrl);
  };

  /*   updateToken = () => {
    return new Promise(async (resolve, reject) => {
      try {
        if (this.getRefreshToken()) {
          const res = await refreshToken(this.getRefreshToken());

          if (res && res.data && res.data.access_token) {
            this.setAccessToken(res.data.access_token, res.data.expires_in);
            this.setRefreshToken(res.data.refresh_token, res.data.refresh_expires_in);
          } else {
            return reject('AUTH FAILED: Refresh token failed');
          }
        } else {
          const { code, sessionState } = this.getCodeAndState();

          if (code) {
            const res = await fetchTokenFromCode(code, sessionState);

            if (res && res.data && res.data.access_token) {
              this.setAccessToken(res.data.access_token, res.data.expires_in);
              this.setRefreshToken(res.data.refresh_token, res.data.refresh_expires_in);
            } else {
              return reject('AUTH FAILED: Fetch token from code failed');
            }
          } else {
            this.requestLogin(true);

            return reject('AUTH FAILED: No code');
          }
        }

        const res = await fetchPermissionToken(this.getAccessToken());

        if (res && res.data && res.data.access_token) {
          this.setPermissionToken(res.data.access_token, res.data.expires_in);
          this.setRefreshToken(res.data.refresh_token, res.data.refresh_expires_in);
          const parseInfo = parseJwt(res.data.access_token);
          const permissionsList = get(parseInfo, 'authorization.permissions') || [];
          const permissions =
            permissionsList.find((it) => it?.rsname == 'api') || permissionsList[0];
          console.log('PERMISSONS', { ...permissions, scopes: permissions?.scopes?.sort() });
          this.setPermissions(permissions.scopes);

          getUserInfo();
        } else {
          return reject('AUTH FAILED: Fetch permission token failed');
        }

        return resolve(this.getPermissionToken());
      } catch (error) {
        Sentry.captureException(error);
        // Not sure if need to logout here
        localStorage.removeItem(TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        localStorage.removeItem(PERMISSION_TOKEN);

        return reject(error);
      }
    });
  }; */

  updateToken = async () => {
    try {
      if (this.getRefreshToken()) {
        const res = await refreshToken(this.getRefreshToken());

        if (!res?.data?.access_token)
          throw new Error('AUTH FAILED: Refresh token failed');

        this.setAccessToken(res.data.access_token, res.data.expires_in);
        this.setRefreshToken(
          res.data.refresh_token,
          res.data.refresh_expires_in
        );
        this.notify();
      } else {
        const { code, sessionState } = this.getCodeAndState();

        if (!code) {
          this.requestLogin(true);
          throw new Error('AUTH FAILED: No code');
        }

        const res = await fetchTokenFromCode(code, sessionState);

        if (!res?.data?.access_token)
          throw new Error('AUTH FAILED: Fetch token from code failed');

        this.setAccessToken(res.data.access_token, res.data.expires_in);
        this.setRefreshToken(
          res.data.refresh_token,
          res.data.refresh_expires_in
        );
      }

      const res = await fetchPermissionToken(this.getAccessToken());

      if (!res?.data?.access_token)
        throw new Error('AUTH FAILED: Fetch permission token failed');

      await this.setPermissionToken(res.data.access_token, res.data.expires_in);
      await this.setRefreshToken(
        res.data.refresh_token,
        res.data.refresh_expires_in
      );
      await this.setPermissionCache(res.data.access_token);

      const userData = await getUserInfo();
      await this.setUserInfo(userData);
      this.notify();
      return this.getPermissionToken();
    } catch (error) {
      Sentry.captureException(error);
      // Not sure if need to logout here
      localStorage.removeItem(TOKEN);
      localStorage.removeItem(REFRESH_TOKEN);
      localStorage.removeItem(PERMISSION_TOKEN);

      throw error;
    }
  };

  notify() {
    if (typeof this.listener === 'function') this.listener();
  }

  subscribe(listener: () => void) {
    this.listener = listener;
    return () => (this.listener = undefined);
  }
}

let keycloak: Keycloak;

const getKeycloak = () => {
  if (keycloak) return keycloak;
  else return new Keycloak();
};

export default getKeycloak();
